import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { IS_IN_IFRAME } from "@src/env";
import { navigationV2 } from "@src/shared/navigation/v2";
import { toErrorMessage } from "@src/shared/utils/errors";
import { events } from "@src/shared/utils/observability/events";

import { authClient } from "../../data";
import type { LoginPageV2Values } from "../../domain/v2";
import { waitForSession } from "../waitForSession";
import { resolvePostAuthDestinationV2 } from "./resolvePostAuthDestinationV2";
import { trackEmailLoginSuccess } from "./trackEmailLoginSuccess";

export function useEmailPasswordLoginFlow() {
  const {
    data: session,
    isPending: isPendingSession,
    error: sessionError,
  } = authClient.useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingSessionDestination, setExistingSessionDestination] = useState<
    string | null
  >(null);

  const redirectTo = searchParams.get("redirectTo");
  const errorFromUrl = searchParams.get("error");
  const sessionErrorMessage = sessionError?.message
    ? "Unable to verify your session. Please try logging in again."
    : undefined;
  const displayError = errorFromUrl || error || sessionErrorMessage || null;

  useEffect(() => {
    if (!session || displayError || IS_IN_IFRAME) return;

    let cancelled = false;

    void resolvePostAuthDestinationV2({
      fallback: navigationV2.home(),
      operation: "existingSessionRedirect",
      redirectTo,
      route: "v2/login",
    })
      .then((destination) => {
        if (!cancelled) {
          setExistingSessionDestination(destination);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setExistingSessionDestination(navigationV2.home());
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, displayError, redirectTo]);

  const submit = async (values: LoginPageV2Values) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const result = await authClient.signIn.email(values);

      if (result.error) {
        const errorMsg = result.error.message ?? "An unknown error occurred";

        setError(errorMsg);
        events.loginFailed.track({ method: "email", error: errorMsg });
        return;
      }

      // A 2FA-enrolled account gets no session from sign-in — only a short-lived
      // two-factor cookie — so login completes on the /2fa page instead.
      if (result.data && "twoFactorRedirect" in result.data) {
        const challengeParams = new URLSearchParams();

        if (redirectTo) challengeParams.set("redirectTo", redirectTo);

        navigate(navigationV2.twoFactor(challengeParams), { replace: true });
        return;
      }

      await Promise.all([trackEmailLoginSuccess(), waitForSession()]);
      const destination = await resolvePostAuthDestinationV2({
        fallback: navigationV2.home(),
        operation: "emailSignIn",
        redirectTo,
        route: "v2/login",
      });
      navigate(destination, { replace: true });
    } catch (err) {
      const errorMsg = toErrorMessage(err);

      setError(errorMsg);
      events.loginFailed.track({ method: "email", error: errorMsg });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    enabled: true,
    error: displayError,
    existingSessionDestination,
    isSubmitting: isSubmitting || isPendingSession,
    submit,
  } as const;
}
